import { Card } from "./Card";
import { MaterialSymbol } from "./MaterialSymbol";
import "./RadioGroup.css";
import { type Accessor, createMemo, For, type JSX, Show } from "solid-js";
import { HStack, Stack, VStack } from "./Stack";
import { Text } from "./typography/Text";

export type RadioOption<T = string | number> = {
	value: T;
	label: string;
	symbol?: string;
	description?: string;
	disabled?: boolean;
	[key: string]: unknown;
};

type RadioGroupProps<T = string | number> = {
	value: T;
	onChange: (value: T) => void;
	options: RadioOption<T>[];
	/** Accessible name when there is no visible group label. */
	ariaLabel?: string;
	/** Id of the element that labels this group (e.g. a field heading). */
	ariaLabelledBy?: string;
	/** Custom label/card content. With `variant="card"`, include a `@ui/Card` for selection styling. */
	children?: (
		option: RadioOption<T>,
		state: {
			isSelected: Accessor<boolean>;
			isDisabled: Accessor<boolean>;
			id: string;
		},
	) => JSX.Element;
	variant?: "default" | "card";
	orientation?: "horizontal" | "vertical";
	autoWidth?: boolean;
	disabled?: boolean;
	name?: string;
	class?: string;
	/** Segmented-button colour theme. Only applies when `variant` is `"default"`. */
	theme?: "blue" | "black";
	style?: JSX.CSSProperties;
};

/**
 * Radio button group for selecting a single option from multiple choices.
 *
 * [Design System →](http://localhost:5174/component/RadioGroup)
 */
export const RadioGroup = <
	T extends string | number | boolean = string | number | boolean,
>(
	props: RadioGroupProps<T>,
) => {
	const theme = props.theme ?? "blue";
	const variant = createMemo(() => props.variant ?? "default");
	const orientation = createMemo(() => props.orientation ?? "horizontal");
	const autoWidth = createMemo(() => props.autoWidth ?? false);
	const isDisabled = createMemo(() => props.disabled ?? false);
	const groupName =
		props.name || `radio-group-${Math.random().toString(36).substring(7)}`;

	return (
		<div
			class={`gvtui-radio-group ${props.class ?? ""}`}
			classList={{
				[`gvtui-radio-group--${orientation()}`]: variant() === "default",
				"gvtui-radio-group--auto-width": autoWidth(),
				"gvtui-radio-group--disabled": isDisabled(),
				"gvtui-radio-group--theme-blue":
					variant() === "default" && theme === "blue",
				"gvtui-radio-group--theme-black":
					variant() === "default" && theme === "black",
				"gvtui-radio-group--card": variant() === "card",
			}}
			role="radiogroup"
			aria-label={props.ariaLabel}
			aria-labelledby={props.ariaLabelledBy}
			aria-disabled={isDisabled()}
			style={props.style}
		>
			<For each={props.options}>
				{(option, index) => {
					const isSelected = createMemo(() => props.value === option.value);
					const optionDisabled = () =>
						isDisabled() || Boolean(option.disabled);
					const radioId = `${groupName}-${index()}`;

					return (
						<div
							class={autoWidth() ? "auto-width" : ""}
							classList={{
								"gvtui-radio-option--disabled": optionDisabled(),
							}}
						>
							<input
								type="radio"
								id={radioId}
								name={groupName}
								value={String(option.value)}
								checked={isSelected()}
								onChange={() => {
									if (optionDisabled()) {
										return;
									}
									props.onChange(option.value);
								}}
								disabled={optionDisabled()}
								class="gvtui-radio-input"
							/>
							<Show
								when={variant() === "card"}
								fallback={
									<label
										for={radioId}
										class={
											!props.children ? "gvtui-radio-label styled filled" : ""
										}
									>
										{props.children ? (
											props.children(option, {
												isSelected,
												isDisabled: optionDisabled,
												id: radioId,
											})
										) : (
											<Stack direction="row" align="center" spacing="small">
												<Show when={option.symbol}>
													{(symbol) => (
														<MaterialSymbol symbol={symbol()} color="white" />
													)}
												</Show>
												{option.label}
											</Stack>
										)}
									</label>
								}
							>
								<label for={radioId} class="gvtui-radio-card-label">
									{props.children ? (
										props.children(option, {
											isSelected,
											isDisabled: optionDisabled,
											id: radioId,
										})
									) : (
										<Card borderRadius="standard">
											<HStack align="center" spacing="large" fullWidth>
												<Show when={option.symbol}>
													{(symbol) => (
														<span class="gvtui-radio-card-icon">
															<MaterialSymbol
																symbol={symbol()}
																relativeSize={1.5}
															/>
														</span>
													)}
												</Show>
												<VStack style={{ flex: "1 0" }}>
													<Text weight="bold" color="ink">
														{option.label}
													</Text>
													<Show when={option.description}>
														<Text size="small" color="fg2">
															{option.description}
														</Text>
													</Show>
												</VStack>
												<span
													class="gvtui-radio-card-indicator"
													aria-hidden="true"
												/>
											</HStack>
										</Card>
									)}
								</label>
							</Show>
						</div>
					);
				}}
			</For>
		</div>
	);
};
